import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// ─── ResizeObserver loop warning suppression ──────────────────────────────
// Chrome fires "ResizeObserver loop completed with undelivered notifications" / "limit
// exceeded" when a callback takes longer than a single animation frame to settle. It's
// harmless but CRA's dev error overlay treats it as fatal. We patch the constructor to
// defer the callback to the next animation frame so the loop never re-enters within
// the same frame. Effective in both dev and production builds.
if (typeof window !== "undefined" && typeof window.ResizeObserver !== "undefined") {
  const OriginalRO = window.ResizeObserver;
  window.ResizeObserver = class PatchedResizeObserver extends OriginalRO {
    constructor(callback) {
      let rafId = 0;
      super((entries, observer) => {
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          rafId = 0;
          try { callback(entries, observer); } catch (_) { /* swallow */ }
        });
      });
    }
  };
}

// Belt-and-suspenders: also intercept any RO errors that slip through (e.g. from
// third-party code that captured a reference to the original RO before our patch).
const RESIZE_OBSERVER_LOOP_ERRORS = [
  "ResizeObserver loop completed with undelivered notifications.",
  "ResizeObserver loop limit exceeded",
];
function isROError(msg) {
  return typeof msg === "string" && RESIZE_OBSERVER_LOOP_ERRORS.some((m) => msg.includes(m));
}
window.addEventListener("error", (e) => {
  if (isROError(e?.message)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
}, true);
window.addEventListener("unhandledrejection", (e) => {
  if (isROError(e?.reason?.message)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
}, true);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
